import { useEffect, useRef, useState, type ReactNode } from 'react';
import { reducedMotion } from '../engine/haptics.ts';
import { MOTION } from '../ui/theme.ts';
import { FitToScreen } from './FitToScreen.tsx';

/**
 * Outgoing screen fades 200 ms, incoming plays fk-in 300 ms — the same both
 * ways, including back. Under prefers-reduced-motion both are instant.
 *
 * `children` is deliberately not an effect dependency: the parent re-renders on
 * every CTA change, and re-running the timer would cancel the swap forever.
 * The outgoing screen is held in a ref that only tracks the step being shown.
 */
export function ScreenTransition({ stepKey, children, scrollable = false }: { stepKey: string; children: ReactNode; scrollable?: boolean }) {
  const reduced = reducedMotion();
  const [shown, setShown] = useState(stepKey);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const held = useRef<ReactNode>(children);

  // While the shown step is the current one, keep the snapshot fresh; during the
  // fade-out it holds the outgoing screen. Writing the ref here (rather than in
  // an effect) is what keeps the outgoing screen on screen for its 200 ms —
  // an effect would swap the content a frame before the fade finished.
  // eslint-disable-next-line react-hooks/refs
  if (stepKey === shown) held.current = children;

  useEffect(() => {
    if (stepKey === shown) return;
    if (reduced) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShown(stepKey);
      return;
    }
    setPhase('out');
    const t = setTimeout(() => {
      setShown(stepKey);
      setPhase('in');
    }, MOTION.fast);
    return () => clearTimeout(t);
  }, [stepKey, shown, reduced]);

  return (
    <main
      key={shown}
      className="fk-main"
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        animation: reduced
          ? 'none'
          : phase === 'in'
            ? `fk-in ${MOTION.screenIn}ms ease-out`
            : `fk-out ${MOTION.fast}ms ease-out forwards`,
      }}
    >
      <FitToScreen resetKey={shown} scroll={scrollable}>
        {/* eslint-disable-next-line react-hooks/refs -- the held step is deliberately read during render so the outgoing screen keeps its content while it animates out */}
        <div style={{ padding: '0 var(--fk-gutter) 24px', display: 'flex', flexDirection: 'column', gap: 'var(--fk-gap)' }}>{held.current}</div>
      </FitToScreen>
    </main>
  );
}
